import React, { useEffect, useMemo, useState } from 'react';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import { renderMarkdown } from './utils/markdown';
import './PlanToProducePage.css';
import './ProductionPlanPage.css';
import './DocsPage.css';

// Documentation (/docs) — System Admin + Owner. Two kinds of content, both taken
// from the server as-is, nothing retyped here:
//   • «API routes» — снимок живого роутера express + раздел прав из access-rules;
//   • остальные пункты — файлы server/docs/*.md (положил .md — появился пункт).
// Выбранный документ живёт в хеше (/docs#calc-2-yoy-forecast) — ссылкой можно поделиться.

const ROUTES = 'routes';   // псевдо-slug первого пункта
const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const fmtDate = v => (v ? new Date(v).toLocaleDateString() : '');
const accessClass = a => (a === 'public' ? 'is-public' : a === 'unmapped' ? 'is-unmapped' : a === 'any logged-in user' ? 'is-any' : '');

const DocsPage = () => {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(() => window.location.hash.slice(1) || ROUTES);
  const [doc, setDoc] = useState(null);
  const [routes, setRoutes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [navOpen, setNavOpen] = useState(false);
  const [q, setQ] = useState('');
  const [method, setMethod] = useState('');

  useEffect(() => {
    api.get('/api/docs')
      .then(r => setDocs(r.data.docs || []))
      .catch(e => setError(e.response?.data?.error || e.message));
  }, []);

  // Навигация назад/вперёд и ссылки вида /docs#calc-2-yoy-forecast
  useEffect(() => {
    const onHash = () => setActive(window.location.hash.slice(1) || ROUTES);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    let alive = true;
    setError('');
    setLoading(true);
    const req = active === ROUTES && routes
      ? Promise.resolve(null)                       // список роутов тянем один раз
      : api.get(`/api/docs/${active}`);
    req
      .then(r => {
        if (!alive || !r) return;
        if (active === ROUTES) setRoutes(r.data.routes || []);
        else setDoc(r.data);
      })
      .catch(e => alive && setError(e.response?.data?.error || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // routes намеренно не в зависимостях: он и есть кеш
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const open = slug => {
    window.location.hash = slug;                    // hashchange сам обновит active
    setNavOpen(false);
    window.scrollTo(0, 0);
  };

  const groups = useMemo(() => {
    const by = new Map();
    docs.forEach(d => { if (!by.has(d.group)) by.set(d.group, []); by.get(d.group).push(d); });
    return [...by.entries()];
  }, [docs]);

  const shownRoutes = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (routes || []).filter(r =>
      (!method || r.method.split(',').includes(method)) &&
      (!s || r.path.toLowerCase().includes(s) || r.access.toLowerCase().includes(s))
    );
  }, [routes, q, method]);

  const html = useMemo(() => (doc ? renderMarkdown(doc.markdown) : ''), [doc]);
  const title = active === ROUTES ? 'API routes' : (doc?.title || '…');

  return (
    <div className="pp-root docs-root">
      <HeaderLogged />
      <div className="ptp-container">
        <div className="ptp-page-header">
          <div>
            <h2 className="ptp-title">Documentation</h2>
            <p className="ptp-subtitle">API routes · agent instructions · how the numbers are calculated</p>
          </div>
          <button className="pp-btn docs-navbtn" onClick={() => setNavOpen(v => !v)}>
            <i className={`fas fa-${navOpen ? 'times' : 'bars'}`} />&nbsp;Contents
          </button>
        </div>

        <div className="docs-layout">
          <nav className={`docs-nav${navOpen ? ' open' : ''}`}>
            <button className={`docs-navlink${active === ROUTES ? ' on' : ''}`} onClick={() => open(ROUTES)}>
              <i className="fas fa-plug" />&nbsp;API routes <span className="pp-muted">(live)</span>
            </button>
            {groups.map(([group, items]) => (
              <div className="docs-group" key={group}>
                <div className="docs-group-title">{group}</div>
                {items.map(d => (
                  <button key={d.slug} className={`docs-navlink${active === d.slug ? ' on' : ''}`} onClick={() => open(d.slug)}>
                    {d.title}
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <main className="docs-main">
            {error && <div className="docs-error">{error}</div>}
            {loading && !error && <div className="docs-empty">Loading…</div>}

            {!loading && !error && active === ROUTES && (
              <>
                <div className="pp-bar docs-bar">
                  <div className="ptp-search-wrapper">
                    <i className="fas fa-search ptp-search-icon" />
                    <input className="ptp-search-input" type="text" placeholder="Search by path or section…"
                           value={q} onChange={e => setQ(e.target.value)} />
                    {q && <button className="ptp-search-clear" onClick={() => setQ('')} title="Clear"><i className="fas fa-times" /></button>}
                  </div>
                  <select value={method} onChange={e => setMethod(e.target.value)}>
                    <option value="">All methods</option>
                    {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <span className="pp-muted docs-count">{shownRoutes.length} of {routes?.length || 0}</span>
                </div>
                <p className="docs-note">
                  Every route the server actually serves, taken from the running app. <b>Access</b> is the
                  section from <code>config/access-rules.js</code>: a user needs that section (view for GET,
                  edit for POST/PUT, delete for DELETE) to call the route.
                </p>
                <div className="docs-table-wrap">
                  <table className="docs-table">
                    <thead><tr><th>Method</th><th>Path</th><th>Access</th><th className="docs-num">MW</th></tr></thead>
                    <tbody>
                      {!shownRoutes.length && <tr><td colSpan={4} className="docs-empty">Nothing matches</td></tr>}
                      {shownRoutes.map((r, i) => (
                        <tr key={`${r.method}${r.path}${i}`}>
                          <td data-label="Method"><span className={`docs-m docs-m--${r.method.split(',')[0].toLowerCase()}`}>{r.method}</span></td>
                          <td data-label="Path"><code>{r.path}</code></td>
                          <td data-label="Access"><span className={`docs-access ${accessClass(r.access)}`}>{r.access}</span></td>
                          <td className="docs-num" data-label="MW">{r.mw}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {!loading && !error && active !== ROUTES && doc && (
              <article className="docs-md">
                <div className="docs-updated">{title} · updated {fmtDate(doc.updated)} · <code>server/docs/{doc.slug}.md</code></div>
                <div dangerouslySetInnerHTML={{ __html: html }} />
              </article>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default DocsPage;
